import React, { useMemo, useState } from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Layout from "../Layout"
import { STORIES, INDUSTRIES, PRODUCTS } from "../../data/caseStudies"
import "../../styles/allCaseStudies.scss"

const ARROW_PATH = '<path d="M5 12h14M13 6l6 6-6 6"/>'
const EXTERNAL_PATH = '<path d="M14 4h6v6M20 4l-9 9"/><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/>'

const Arrow = ({ size = 20 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: ARROW_PATH }}
  />
)

// Tiles' background tint, assigned in render order to every tile EXCEPT the
// big featured one — cycles every 8 tiles (~2/5 tinted, 3/5 white).
const TINT_CYCLE = ["white", "cream", "white", "pale-blue", "white", "warm-grey", "white", "pale-blue"]

// Pure: returns one 12-column span per tile in `count`, in order. A
// repeating cycle of row patterns (each summing to 12) — [4,4,4], [3,3,6],
// [4,4,4], [3,3,3,3] — then back to the first; an incomplete trailing row
// (fewer tiles left than the next pattern needs) is stretched instead so it
// still sums to 12: 1 tile -> 6, 2 tiles -> 6+6, 3 tiles -> 4+4+4. Used for
// every tile after the featured special-case (or from the first tile when
// a non-"All" filter is active) — never a per-client lookup table.
const ROW_PATTERNS = [
  [4, 4, 4],
  [3, 3, 6],
  [4, 4, 4],
  [3, 3, 3, 3],
]

function getRowSpans(count) {
  const spans = []
  let remaining = count
  let i = 0
  while (remaining > 0) {
    const pattern = ROW_PATTERNS[i % ROW_PATTERNS.length]
    if (remaining >= pattern.length) {
      spans.push(...pattern)
      remaining -= pattern.length
      i += 1
    } else if (remaining === 1) {
      spans.push(6)
      remaining = 0
    } else if (remaining === 2) {
      spans.push(6, 6)
      remaining = 0
    } else {
      spans.push(4, 4, 4)
      remaining = 0
    }
  }
  return spans
}

/**
 * /caseStudies — every client story and product in one place, v2 visual
 * redesign (src/styles/allCaseStudies.scss). Whether a story or product
 * gets a "Read the case study"/"See the product" call to action is decided
 * here, once, from `hasPagePaths` (built by the page query from
 * allSitePage — see src/pages/caseStudies/index.js) — nothing about which
 * stories have pages is hardcoded, so a page added later gains its link
 * automatically. This part, and the industry filter itself, are UNCHANGED
 * from before this redesign.
 *
 * The industry filter is plain React state, not vanilla JS: every card is
 * still in the initial server-rendered HTML regardless of state (search
 * engines and a no-JS browser see everything), and the `hidden` attribute —
 * not a CSS class, not display:none in a stylesheet — is what the filter
 * toggles. Grid placement, though, has to be reactive: a hidden tile drops
 * out of grid flow entirely, so each tile's column span is computed fresh
 * per `activeFilter` against only the tiles that filter actually shows
 * (see the `layout` memo below), via the pure `getRowSpans` above.
 *
 * The previous design's client-logo chip (useClientLogos) has no place in
 * this redesign's three card types (none of them show one) and would also
 * break the page's "calm palette" rule if a colour logo rendered on a
 * card — dropped here, flagged in this ticket's final report.
 */
const AllCaseStudiesPage = ({ photos, hasPagePaths }) => {
  const [activeFilter, setActiveFilter] = useState("all")

  const hasPage = path => hasPagePaths.has(path.replace(/\/$/, ""))

  const counts = useMemo(() => {
    const byIndustry = {}
    STORIES.forEach(s => {
      byIndustry[s.industry] = (byIndustry[s.industry] || 0) + 1
    })
    return byIndustry
  }, [])

  const featured = STORIES.find(s => s.featured)
  const rest = STORIES.filter(s => !s.featured)
  // Stories with a page first, then stories without one — otherwise the
  // original order is kept (a stable sort, not a re-sort by name/industry).
  const ordered = useMemo(
    () => [...rest].sort((a, b) => (hasPage(b.path) ? 1 : 0) - (hasPage(a.path) ? 1 : 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rest, hasPagePaths]
  )

  // Fixed DOM/render order — every card always renders, filter or no
  // filter (hidden toggles visibility, never removes a card from markup).
  const allInOrder = useMemo(() => (featured ? [featured, ...ordered] : ordered), [featured, ordered])

  // Per-tile grid placement + tint + card type, recomputed per active
  // filter against only the tiles that filter will actually show.
  const layout = useMemo(() => {
    const map = new Map()
    const typeFor = col => (col === 3 ? "vertical" : "horizontal")

    if (activeFilter === "all" && featured) {
      map.set(featured, { colSpan: 6, rowSpan: 2, type: "featured" })
      const firstFour = ordered.slice(0, 4)
      firstFour.forEach((s, idx) => {
        map.set(s, { colSpan: 3, rowSpan: 1, type: "vertical", tint: TINT_CYCLE[idx % TINT_CYCLE.length] })
      })
      const remainder = ordered.slice(4)
      const spans = getRowSpans(remainder.length)
      remainder.forEach((s, idx) => {
        map.set(s, {
          colSpan: spans[idx],
          rowSpan: 1,
          type: typeFor(spans[idx]),
          tint: TINT_CYCLE[(idx + 4) % TINT_CYCLE.length],
        })
      })
    } else {
      const visible = allInOrder.filter(s => activeFilter === "all" || s.industry === activeFilter)
      const spans = getRowSpans(visible.length)
      visible.forEach((s, idx) => {
        map.set(s, {
          colSpan: spans[idx],
          rowSpan: 1,
          type: typeFor(spans[idx]),
          tint: TINT_CYCLE[idx % TINT_CYCLE.length],
        })
      })
    }
    return map
  }, [activeFilter, featured, ordered, allInOrder])

  const renderFigureOrDesc = story => {
    if (story.metrics && story.metrics.length) {
      const m = story.metrics[0]
      return (
        <>
          <span className="cs-all__tile-figure">{m.value}</span>
          <p className="cs-all__tile-desc">{m.label}</p>
        </>
      )
    }
    return <p className="cs-all__tile-desc cs-all__tile-desc--clamp">{story.summary}</p>
  }

  const renderVerticalCard = (story, hidden, { colSpan, rowSpan }) => {
    const linked = hasPage(story.path)
    const photo = photos[story.photoFile]
    const Tag = linked ? Link : "div"
    const tagProps = linked ? { to: story.path } : {}

    return (
      <Tag
        key={story.name}
        {...tagProps}
        className="cs-all__tile cs-all__tile--vertical"
        style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
        data-industry={story.industry}
        hidden={hidden || undefined}
      >
        <div className="cs-all__tile-photo">
          {photo && <GatsbyImage image={photo} alt="" loading="lazy" className="cs-all__tile-photo-img" />}
          <span className="cs-all__tile-tag">{story.industry}</span>
          <span className="cs-all__tile-arrow cs-all__tile-arrow--lg">
            <Arrow size={14} />
          </span>
        </div>
        <div className="cs-all__tile-body">
          <h3 className="cs-all__tile-name">{story.name}</h3>
          {renderFigureOrDesc(story)}
        </div>
      </Tag>
    )
  }

  const renderHorizontalCard = (story, hidden, { colSpan, rowSpan, tint }) => {
    const linked = hasPage(story.path)
    const photo = photos[story.photoFile]
    const Tag = linked ? Link : "div"
    const tagProps = linked ? { to: story.path } : {}
    const hClass = colSpan === 6 ? "cs-all__tile--h6" : "cs-all__tile--h4"

    return (
      <Tag
        key={story.name}
        {...tagProps}
        className={`cs-all__tile cs-all__tile--horizontal ${hClass} cs-all__tile--${tint}`}
        style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
        data-industry={story.industry}
        hidden={hidden || undefined}
      >
        <div className="cs-all__tile-photo">
          {photo && <GatsbyImage image={photo} alt="" loading="lazy" className="cs-all__tile-photo-img" />}
          <span className="cs-all__tile-tag">{story.industry}</span>
        </div>
        <div className="cs-all__tile-text">
          <div className="cs-all__tile-row-top">
            <h3 className="cs-all__tile-name cs-all__tile-name--md">{story.name}</h3>
            <span className="cs-all__tile-arrow cs-all__tile-arrow--md">
              <Arrow size={14} />
            </span>
          </div>
          <div className="cs-all__tile-row-bottom">
            {story.metrics && story.metrics.length ? (
              <>
                <span className="cs-all__tile-figure cs-all__tile-figure--md">{story.metrics[0].value}</span>
                <p className="cs-all__tile-desc">{story.metrics[0].label}</p>
              </>
            ) : (
              <p className="cs-all__tile-desc cs-all__tile-desc--clamp">{story.summary}</p>
            )}
            {story.built && (
              <div className="cs-all__tile-pills">
                {story.built.split(" · ").map(tag => (
                  <span key={tag} className="cs-all__tile-pill">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Tag>
    )
  }

  const renderFeaturedTile = (story, hidden, { colSpan, rowSpan }) => {
    const linked = hasPage(story.path)
    const photo = photos[story.photoFile]
    const Tag = linked ? Link : "div"
    const tagProps = linked ? { to: story.path } : {}

    return (
      <Tag
        key={story.name}
        {...tagProps}
        className="cs-all__tile cs-all__tile--featured"
        style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
        data-industry={story.industry}
        hidden={hidden || undefined}
      >
        <div className="cs-all__feat-photo">
          {photo && <GatsbyImage image={photo} alt="" loading="eager" className="cs-all__feat-photo-img" />}
          <div className="cs-all__feat-pills">
            <span className="cs-all__feat-pill cs-all__feat-pill--story">Featured story</span>
            <span className="cs-all__feat-pill cs-all__feat-pill--industry">
              {story.industry}
              {story.location ? ` · ${story.location}` : ""}
            </span>
          </div>
          <span className="cs-all__tile-arrow cs-all__feat-arrow">
            <Arrow size={14} />
          </span>
        </div>
        <div className="cs-all__feat-foot">
          <div className="cs-all__feat-text">
            {/* h2, not h3 like the other tiles — keeps a valid heading order
                (h1 hero -> h2 featured story -> h3 every other tile -> h2
                products -> h2 closing band), same as before this redesign. */}
            <h2 className="cs-all__feat-name">{story.name}</h2>
            <p className="cs-all__feat-summary">{story.summary}</p>
          </div>
          <div className="cs-all__feat-metrics">
            {story.metrics.slice(0, 2).map((m, idx) => (
              <div key={m.label} className="cs-all__feat-metric">
                <b className="cs-all__feat-figure">{m.value}</b>
                <span className={`cs-all__feat-label cs-all__feat-label--${idx + 1}`}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Tag>
    )
  }

  const renderTile = story => {
    const hidden = activeFilter !== "all" && story.industry !== activeFilter
    const placement = layout.get(story) || { colSpan: 3, rowSpan: 1, type: "vertical", tint: "white" }
    if (placement.type === "featured") return renderFeaturedTile(story, hidden, placement)
    if (placement.type === "horizontal") return renderHorizontalCard(story, hidden, placement)
    return renderVerticalCard(story, hidden, placement)
  }

  // P4 — fixed per-position spans, explicitly given in this order by the
  // design (unlike the stories grid, which must never hardcode spans per
  // client): VisioEdge 6, Viztos 3, Distribution Utility 3, Class
  // Management 3, Donation Campaign Management 3, 360SafeOps 6.
  const PRODUCT_SPANS = [6, 3, 3, 3, 3, 6]
  const PRODUCT_TINTS = {
    VisioEdge: "pale-blue",
    Viztos: "white",
    "Distribution Utility": "white",
    "Class Management": "white",
    "Donation Campaign Management": "warm-grey",
    "360SafeOps": "cream",
  }

  return (
    <Layout
      pageTitle="Visiomate - Case Studies"
      descrip="See how Visiomate has helped manufacturers, retailers, distributors, healthcare practices and more with Odoo, Dynamics 365 and custom software."
    >
      <div className="cs-all">
        {/* P1. HERO */}
        <section className="cs-all__hero">
          <span className="cs-all__hero-ring" aria-hidden="true" />
          <div className="cs-all__hero-inner">
            <div className="cs-all__hero-text">
              <p className="cs-all__eyebrow">Case studies</p>
              <h1 className="cs-all__h1">Work we've delivered.</h1>
              <p className="cs-all__hero-sub">
                Real systems for real businesses, from bakeries and footwear chains to a deep-sea port, a
                psychology practice and a fencing franchise.
              </p>
            </div>
            <div className="cs-all__stats">
              <div className="cs-all__stat">
                <b className="cs-all__stat-num">{STORIES.length}</b>
                <span className="cs-all__stat-lbl">client stories</span>
              </div>
              <div className="cs-all__stat">
                <b className="cs-all__stat-num">{INDUSTRIES.length}</b>
                <span className="cs-all__stat-lbl">industries</span>
              </div>
              <div className="cs-all__stat">
                <b className="cs-all__stat-num">{PRODUCTS.length}</b>
                <span className="cs-all__stat-lbl">products</span>
              </div>
            </div>
          </div>
        </section>

        {/* P2. FILTER STRIP */}
        <nav className="cs-all__filters" aria-label="Filter case studies by industry">
          <div className="cs-all__filters-inner">
            <button
              type="button"
              className={`cs-all__chip${activeFilter === "all" ? " cs-all__chip--on" : ""}`}
              aria-pressed={activeFilter === "all"}
              onClick={() => setActiveFilter("all")}
            >
              All <span className="cs-all__chip-count">{STORIES.length}</span>
            </button>
            {INDUSTRIES.map(ind => (
              <button
                key={ind}
                type="button"
                className={`cs-all__chip${activeFilter === ind ? " cs-all__chip--on" : ""}`}
                aria-pressed={activeFilter === ind}
                onClick={() => setActiveFilter(ind)}
              >
                {ind} <span className="cs-all__chip-count">{counts[ind] || 0}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* P3. STORIES GRID */}
        <section className="cs-all__grid-section">
          <div className="cs-all__grid-inner">
            <div className="cs-all__grid">{allInOrder.map(renderTile)}</div>
          </div>
        </section>

        {/* P4. PRODUCTS */}
        <section className="cs-all__prod">
          <div className="cs-all__prod-inner">
            <div className="cs-all__prod-head">
              <div>
                <p className="cs-all__eyebrow">Our products</p>
                <h2 className="cs-all__prod-heading">Built by us, ready to use.</h2>
              </div>
              <p className="cs-all__prod-sub">Products we've built and run for clients, available for your business too.</p>
            </div>
            <div className="cs-all__pgrid">
              {PRODUCTS.map((p, idx) => {
                const isExternal = !!p.externalUrl
                const linked = isExternal || (p.path && hasPage(p.path))
                const Tag = isExternal ? "a" : linked ? Link : "div"
                const tagProps = isExternal
                  ? { href: p.externalUrl, target: "_blank", rel: "noopener noreferrer" }
                  : linked
                  ? { to: p.path }
                  : {}
                const tint = PRODUCT_TINTS[p.name] || "white"
                return (
                  <Tag
                    key={p.name}
                    {...tagProps}
                    className={`cs-all__pcard cs-all__pcard--${tint}`}
                    style={{ gridColumn: `span ${PRODUCT_SPANS[idx] || 3}` }}
                  >
                    <div className="cs-all__pcard-top">
                      <h3 className="cs-all__pcard-title">{p.name}</h3>
                      <span className="cs-all__pcard-arrow">
                        <Arrow size={14} />
                      </span>
                    </div>
                    <div>
                      <p className="cs-all__pcard-desc">{p.description}</p>
                      {isExternal ? (
                        <span className="cs-all__pcard-link">
                          Visit the website
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: EXTERNAL_PATH }}
                          />
                          <span className="cs-all__sr-only">(opens in a new tab)</span>
                        </span>
                      ) : linked ? (
                        <span className="cs-all__pcard-link">
                          See the product <Arrow size={16} />
                        </span>
                      ) : null}
                    </div>
                  </Tag>
                )
              })}
            </div>
          </div>
        </section>

        {/* P7. CLOSING BAND */}
        <section className="cs-all__cta">
          <div className="cs-all__cta-inner">
            <div>
              <h2 className="cs-all__cta-heading">Have a similar challenge?</h2>
              <p className="cs-all__cta-detail">The Fit Sprint gives you a costed plan before you commit to anything.</p>
            </div>
            <Link className="cs-all__cta-btn" to="/offers/ops-and-erp-fit-sprint">
              See the Fit Sprint
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default AllCaseStudiesPage
